import styled from '@emotion/styled';
import {css} from '@emotion/react';
import {Link as JoyLink} from '@mui/joy';
import {defaultTheme} from '../theme/default';
import {ReactComponent as ArrowSVG} from '../../assets/icons/ic_arrow_small_right.svg';

export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ellipsisStyle = css`
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type ImageProps = {
  url: string;
};

export const Logo = styled.div<ImageProps>`
  background: url(${(props) => props.url}) center center;
  background-size: contain;
  background-repeat: no-repeat;
`;

export const CardLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const IconRightArrow = styled(ArrowSVG)`
  width: 16px;
  height: 16px;
  fill: ${({theme}) => theme.color.primary};
`;

// *****************************************
// TYPOGRAPHY
// *****************************************

export const TextColors = {
  primary: defaultTheme.color.neutral.grey_900,
  secondary: defaultTheme.color.neutral.grey_600,
  disabled: defaultTheme.color.neutral.grey_500,
  onDark: defaultTheme.color.neutral.grey_50,
  brand: defaultTheme.color.primary,
  brandHover: defaultTheme.color.primary_70,
  white: defaultTheme.color.white,
};

// types
type Props = {
  color?: (typeof TextColors)[keyof typeof TextColors];
  bold?: 'bold' | 'normal';
};

const baseStyle = css`
  margin: 0;
  padding: 0;
`;

export const TitleExtraLarge = styled.p<Props>`
  ${baseStyle};
  font-weight: ${({theme, bold}) =>
    bold
      ? theme.typography.fontWeight[bold]
      : theme.typography.fontWeight.bold};
  color: ${({color}) => color ?? TextColors.primary};

  // For larger screens
  @media screen and (min-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 40px;
    line-height: 48px;
  }

  // For smaller screens
  @media screen and (max-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 32px;
    line-height: 40px;
  }
`;

export const TitleLarge = styled.p<Props>`
  ${baseStyle};
  font-weight: ${({theme, bold}) =>
    bold
      ? theme.typography.fontWeight[bold]
      : theme.typography.fontWeight.bold};
  color: ${({color}) => color ?? TextColors.primary};

  // For larger screens
  @media screen and (min-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 28px;
    line-height: 36px;
  }

  // For smaller screens
  @media screen and (max-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 24px;
    line-height: 32px;
  }
`;

export const Subtitle = styled.p<Props>`
  ${baseStyle};
  color: ${({color}) => color ?? TextColors.primary};
  font-weight: ${({theme, bold}) =>
    bold
      ? theme.typography.fontWeight[bold]
      : theme.typography.fontWeight.normal};

  // For larger screens
  @media screen and (min-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 20px;
    line-height: 28px;
  }

  // For smaller screens
  @media screen and (max-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 18px;
    line-height: 26px;
  }
`;

export const TextDefault = styled.p<Props>`
  ${baseStyle};
  color: ${({color}) => color ?? TextColors.primary};
  font-weight: ${({theme, bold}) =>
    bold
      ? theme.typography.fontWeight[bold]
      : theme.typography.fontWeight.normal};

  // For larger screens
  @media screen and (min-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 16px;
    line-height: 24px;
  }

  // For smaller screens
  @media screen and (max-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 15px;
    line-height: 22px;
  }
`;

export const TextSmall = styled.p<Props>`
  ${baseStyle};
  color: ${({color}) => color ?? TextColors.primary};
  font-weight: ${({theme, bold}) =>
    bold ? theme.typography.fontWeight[bold] : 'inherit'};
  font-size: 14px;
  line-height: 20px;
`;

export const TextExtraSmall = styled.p<Props>`
  ${baseStyle};
  color: ${({color}) => color ?? TextColors.primary};
  font-weight: ${({theme, bold}) =>
    bold
      ? theme.typography.fontWeight[bold]
      : theme.typography.fontWeight.normal};
  font-size: 12px;
  line-height: 16px;
`;

export const LinkPrimary = styled(JoyLink)`
  color: ${({theme}) => theme.color.primary};
  font-weight: ${({theme}) => theme.typography.fontWeight.bold};
  text-decoration: none;
  text-decoration-skip-ink: none;
  margin: 0;

  :hover {
    color: ${({theme}) => theme.color.primary_70};
  }

  // For larger screens
  @media screen and (min-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 16px;
    line-height: 24px;
  }

  // For smaller screens
  @media screen and (max-width: ${({theme}) => theme.screenBreakpoint.md.px}) {
    font-size: 16px;
    line-height: 22px;
  }
`;

export const LinkSecondary = styled(JoyLink)`
  color: ${({theme}) => theme.color.neutral.grey_600};
  font-weight: ${({theme}) => theme.typography.fontWeight.bold};
  text-decoration: underline;
  text-decoration-skip-ink: none;
  font-size: 14px;
  line-height: 16px;
`;
